const API_BASE = '/api/compliance/tasks';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

function getAuthHeaders(): HeadersInit {
  const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(err.error || err.message || `API Error: ${res.status}`);
  }
  return res.json();
}

export interface ComplianceTask {
  id: number;
  company_id: number;
  compliance_page_id: number;
  framework_assignment_id: number;
  title: string;
  description?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  due_date?: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  completed_at?: string;
  created_at: string;
}

export interface TaskStats {
  total: number;
  pending: number;
  in_progress: number;
  completed: number;
  overdue: number;
}

export const tasksApi = {
  async listTasks(params?: { page?: number; limit?: number; status?: string; priority?: string; company_id?: number; overdue?: boolean }): Promise<{ tasks: ComplianceTask[]; total: number; page: number; pages: number }> {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    if (params?.priority) q.set('priority', params.priority);
    if (params?.company_id) q.set('company_id', String(params.company_id));
    if (params?.overdue) q.set('overdue', 'true');
    const res = await fetch(`${API_BASE}?${q}`, { headers: getAuthHeaders() });
    return handleResponse(res);
  },

  async createTask(data: Partial<ComplianceTask>): Promise<ComplianceTask> {
    const res = await fetch(API_BASE, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getTask(id: number): Promise<ComplianceTask> {
    const res = await fetch(`${API_BASE}/${id}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateTask(id: number, data: Partial<ComplianceTask>): Promise<ComplianceTask> {
    const res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(data) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async updateStatus(id: number, status: string): Promise<ComplianceTask> {
    const res = await fetch(`${API_BASE}/${id}/status`, { method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify({ status }) });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getOverdue(): Promise<ComplianceTask[]> {
    const res = await fetch(`${API_BASE}/overdue`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getUpcoming(days = 7): Promise<ComplianceTask[]> {
    const res = await fetch(`${API_BASE}/upcoming?days=${days}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },

  async getStats(companyId?: number): Promise<TaskStats> {
    const q = companyId ? `?company_id=${companyId}` : '';
    const res = await fetch(`${API_BASE}/stats${q}`, { headers: getAuthHeaders() });
    const result: any = await handleResponse(res);
    return result.data || result;
  },
};
